import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { media } from '../../utils/media';
import { FaBars, FaTimes, FaQuestionCircle } from 'react-icons/fa';
import PropTypes from 'prop-types';
import { Tooltip } from '@mui/material';

const DashboardNavBar = ({ hasRecordingsAccess = false, hasSubscriptionAccess = false, hasAdminSubscriptionAccess = false }) => {
  const [showSidebar, setShowSidebar] = useState(false);
  const location = useLocation();

  const handleToggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  return (
    <NavContainer>
      <LogoContainer>
        <Logo>My Videos</Logo>
        <Tooltip 
          title={
            <div style={{ padding: '8px', fontSize: '14px', lineHeight: '1.4' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>My Videos Dashboard</div>
              <div style={{ marginBottom: '8px' }}>Access your purchased videos, manage recordings, leave feedback, and manage your subscription all in one place.</div>
              <div style={{ fontStyle: 'italic', color: '#888', fontSize: '12px', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #333' }}>
                <div style={{ marginBottom: '4px' }}>If you are experiencing download issues, please try desktop or another mobile browser.</div>
                <div>Leave us feedback below or via the "Leave Feedback" tab.</div>
              </div>
            </div>
          }
          arrow
          placement="bottom-start"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'rgba(0, 0, 0, 0.9)',
                '& .MuiTooltip-arrow': {
                  color: 'rgba(0, 0, 0, 0.9)',
                },
              },
            },
          }}
        >
          <QuestionIcon>
            <FaQuestionCircle />
          </QuestionIcon>
        </Tooltip>
      </LogoContainer>
      <HamburgerIcon onClick={handleToggleSidebar}>
        {showSidebar ? <FaTimes /> : <FaBars />}
      </HamburgerIcon>
      <NavLinks $showSidebar={showSidebar}>
        <StyledNavLink 
          to={`/dashboard/purchased-videos${location.search}`}
          className={({ isActive }) => (isActive ? 'active' : '')}
        >
          Purchased Videos
        </StyledNavLink>
        <StyledNavLink 
          to={`/dashboard/feedback${location.search}`}
          className={({ isActive }) => (isActive ? 'active' : '')}
        >
          Leave Feedback
        </StyledNavLink>
        {hasSubscriptionAccess && (
          <StyledNavLink 
            to={`/dashboard/subscription${location.search}`}
            className={({ isActive }) => (isActive ? 'active' : '')}
          >
            My Subscription
          </StyledNavLink>
        )}
        {hasAdminSubscriptionAccess && (
          <StyledNavLink 
            to={`/dashboard/admin-subscription${location.search}`}
            className={({ isActive }) => (isActive ? 'active' : '')}
          >
            Admin Subscriptions
          </StyledNavLink>
        )}
        {hasRecordingsAccess && (
          <StyledNavLink 
            to={`/dashboard/recordings${location.search}`}
            className={({ isActive }) => (isActive ? 'active' : '')}
          >
            Recordings
          </StyledNavLink>
        )}
      </NavLinks>
    </NavContainer>
  );
};

DashboardNavBar.propTypes = {
  hasRecordingsAccess: PropTypes.bool,
  hasSubscriptionAccess: PropTypes.bool,
  hasAdminSubscriptionAccess: PropTypes.bool
};

export default DashboardNavBar;

/* --------------------------------------------
 * STYLES
 * -------------------------------------------- */

const NavContainer = styled.nav`
  background-color: ${({ theme }) => theme.colors.primaryBackground};
  padding: 1.2rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  ${media.mobile`
    padding: 1rem;
  `}
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Logo = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: ${({ theme }) => theme.fonts.primary};
  background: linear-gradient(45deg, ${({ theme }) => theme.colors.accentBlue}, ${({ theme }) => theme.colors.accentOrange});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.06);
  }

  ${media.mobile`
    font-size: ${({ theme }) => theme.fontSizes.lg};
  `}
`;

const QuestionIcon = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 16px;
  cursor: pointer;
  transition: color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;

  &:hover {
    color: ${({ theme }) => theme.colors.accentOrange};
  }

  ${media.mobile`
    font-size: 14px;
    width: 18px;
    height: 18px;
  `}
`;

const HamburgerIcon = styled.div`
  display: none;
  font-size: 1.8rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.accentOrange};
  }

  ${media.mobile`
    display: block;
  `}
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;

  ${media.mobile`
    position: fixed;
    top: 60px;
    left: ${({ $showSidebar }) => ($showSidebar ? '0' : '-100%')};
    width: 100%;
    height: calc(100vh - 60px);
    background-color: ${({ theme }) => theme.colors.primaryBackground};
    flex-direction: column;
    padding: 2rem 1rem;
    transition: left 0.3s ease;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  `}
`;

const StyledNavLink = styled(NavLink)`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  position: relative;
  font-weight: 500;

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
    background-color: ${({ theme }) => theme.colors.hover};
  }

  &.active {
    color: ${({ theme }) => theme.colors.accentOrange};
    background-color: ${({ theme }) => theme.colors.hover};
    font-weight: 600;
  }

  ${media.mobile`
    width: 100%;
    text-align: center;
    padding: 1rem;
    font-size: ${({ theme }) => theme.fontSizes.lg};
  `}
`;
